import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { RecordLinkFields } from './RecordLinkFields';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { formatBytes } from './utils';
import { useConfirm } from '@/components/ConfirmDialog';

export const DOC_CATEGORIES = ['Permits', 'Contracts', 'Plans', 'Financial', 'Photos', 'Internal Notes', 'Other'];
const PERMIT_TYPES = ['DOB Filing', 'DOT Permit'];
const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Section C: editable document detail panel with autosave + clickable file field.
export function DocumentDetailSheet({ open, onOpenChange, doc, onChanged, onDeleted, contextPropertyId = null }) {
  const confirm = useConfirm();
  const [local, setLocal] = useState(doc || {});
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => { setLocal(doc || {}); }, [doc]);

  const isPermitLinked = PERMIT_TYPES.includes(local.record_type);
  const lr = local.linked_record;

  const save = async (patch) => {
    setLocal(prev => ({ ...prev, ...patch }));
    try {
      const r = await api.put(`/documents/${doc.id}`, patch);
      setLocal(prev => ({ ...prev, ...r.data }));
      onChanged && onChanged(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  // Only persist a record link when both fields are set together, or both cleared.
  const onRecordChange = (rt, rid) => {
    setLocal(prev => ({ ...prev, record_type: rt, record_id: rid }));
    if (!rt && !rid) return save({ record_type: null, record_id: null });
    if (rt && rid) return save({ record_type: rt, record_id: rid });
  };

  const handleDelete = async () => {
    const ok = await confirm({ title: 'Delete document', description: 'Delete this document?', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    await api.delete(`/documents/${doc.id}`);
    toast.success('Document deleted');
    onOpenChange(false);
    onDeleted && onDeleted();
  };

  if (!doc) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-lg bg-card border-border overflow-y-auto" data-testid="document-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading truncate pr-6" data-testid="doc-detail-title">{local.name || 'Document'}</SheetTitle>
            <SheetDescription>Document details</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input value={local.name || ''} onChange={e => setLocal({ ...local, name: e.target.value })} onBlur={e => save({ name: e.target.value })} className="bg-background border-border" data-testid="doc-detail-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={local.category || 'Other'} onValueChange={v => save({ category: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="doc-detail-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{DOC_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <RecordLinkFields recordType={local.record_type || null} recordId={local.record_id || null} onChange={onRecordChange} testidPrefix="doc-detail" contextPropertyId={contextPropertyId} />

            {/* Section C: live read-only summary of the linked DOB Filing / DOT Permit */}
            {isPermitLinked && (
              <div className="rounded-md border border-border bg-secondary/20 p-3 space-y-2" data-testid="doc-linked-record-summary">
                <p className="text-xs font-semibold" style={{ color: '#031333' }}>Linked {local.record_type}</p>
                {lr ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <span className="text-xs text-muted-foreground block">Status</span>
                      <span className="text-sm" style={{ color: '#010A21' }} data-testid="linked-status">{lr.status || '\u2014'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Expiration Date</span>
                      <span className="text-sm" style={{ color: '#010A21', fontFamily: "'DM Mono', monospace" }} data-testid="linked-expiration">{lr.expiration_date || '\u2014'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">{lr.number_label || 'Number'}</span>
                      <span className="text-sm" style={{ color: '#010A21', fontFamily: "'DM Mono', monospace" }} data-testid="linked-number">{lr.number || '\u2014'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Issue Date</span>
                      <span className="text-sm" style={{ color: '#010A21', fontFamily: "'DM Mono', monospace" }} data-testid="linked-issue-date">{lr.issue_date || '\u2014'}</span>
                    </div>
                    {lr.cost !== null && lr.cost !== undefined && (
                      <div>
                        <span className="text-xs text-muted-foreground block">Cost</span>
                        <span className="text-sm" style={{ color: '#010A21', fontFamily: "'DM Mono', monospace" }} data-testid="linked-cost">{fmtMoney(lr.cost)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="linked-record-missing">Linked record not found.</p>
                )}
                <p className="text-[11px] text-muted-foreground pt-0.5">Live values from the linked record. Refreshing the permit updates these automatically.</p>
              </div>
            )}

            {/* File field: clickable to open preview */}
            <div>
              <Label className="text-xs text-muted-foreground">File</Label>
              <button type="button" onClick={() => setPreviewOpen(true)} className="w-full flex items-center gap-2 mt-1 p-2 rounded-md border border-border bg-background hover:bg-secondary/40 transition-colors text-left" data-testid="doc-detail-file-preview">
                <FileText className="h-4 w-4 text-primary shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="text-sm truncate block" style={{ color: '#010A21' }} data-testid="doc-detail-file-name">{local.original_filename || 'No file attached'}</span>
                  {local.file_size ? <span className="text-xs text-muted-foreground" data-testid="doc-detail-file-size">{formatBytes(local.file_size)}</span> : null}
                </span>
                <Eye className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              </button>
            </div>

            {isPermitLinked ? (
              <div>
                <Label className="text-xs text-muted-foreground">Expiration Date</Label>
                <p className="text-sm mt-1.5" style={{ color: '#010A21', fontFamily: "'DM Mono', monospace" }} data-testid="doc-detail-expiration-readonly">
                  {(lr && lr.expiration_date) || '\u2014'}
                  <span className="text-xs text-muted-foreground ml-2" style={{ fontFamily: 'inherit' }}>from linked {local.record_type}</span>
                </p>
              </div>
            ) : (
              <div>
                <Label className="text-xs text-muted-foreground">Expiration Date <span className="text-muted-foreground/60">(optional)</span></Label>
                <Input type="date" value={(local.expiration_date || '').slice(0, 10)} onChange={e => setLocal({ ...local, expiration_date: e.target.value })} onBlur={e => save({ expiration_date: e.target.value })} className="bg-background border-border" data-testid="doc-detail-expiration" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <Label className="text-xs text-muted-foreground">Upload Date</Label>
                <p className="text-sm mt-1" style={{ color: '#010A21' }} data-testid="doc-detail-upload-date">{local.created_at ? new Date(local.created_at).toLocaleDateString() : '\u00A0'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <p className="text-sm mt-1" style={{ color: '#010A21' }}>{local.status || 'Active'}</p>
              </div>
            </div>

            <div className="pt-2">
              <Button variant="outline" className="text-destructive hover:text-destructive border-border" onClick={handleDelete} data-testid="doc-detail-delete">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Document
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <DocumentPreviewModal open={previewOpen} onOpenChange={setPreviewOpen} doc={local} />
    </>
  );
}

export default DocumentDetailSheet;
